import { type SorterResult, type SortOrder, type TableRowSelection } from "antd/es/table/interface";
import {
    type BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import {
    getOwnerOptions,
    getShowOptions,
    getSubsOptions,
} from "legacyComponents/FileUploadContainer.utils";
import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Sortable } from "react-sortablejs";

import { BTButton } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { BTLoginTypes, type MediaType } from "types/enum";

import { useTracking } from "utilities/analytics/analytics";

import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTable, type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import {
    HiddenMediaContextMenu,
    MediaContextMenu,
} from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu";
import {
    areContextItemsPresent,
    getAttachmentOverlayItems,
} from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";
import { AttachmentOverlay } from "commonComponents/entity/media/common/MediaContextMenu/Overlays/AttachmentOverlay";
import {
    addedByColumn,
    lastModifiedColumn,
    permissionsColumn,
    thumbnailColumn,
    titleColumn,
} from "commonComponents/entity/media/common/MediaTable/MediaTableColumns";
import { useAttachedFileClickHandler } from "commonComponents/entity/media/common/useAttachedFileClickHandler";
import { type SharingSettingsAPI } from "commonComponents/entity/sharing/SharingContainer/SharingContainer.types";
import { DragAndDropHandle } from "commonComponents/utilities/DragAndDrop/DragAndDrop";

import {
    areAllFilesSelected,
    canClickFile,
    canMultiSelectFiles,
    handleSelectAllFiles,
    hasSelectedFiles,
} from "entity/media/common/media.utils";
import { type AnnotationType } from "entity/media/common/mediaTypes";

import "./MediaTable.less";

interface IMediaTableProps {
    files: BTFileSystem[];
    onOpenLightbox: (id: number) => void;
    /** Return true to suppress the default click handling (lightbox/route/download). */
    onFileClick?: (file: BTFileSystem) => boolean;
    onVideoDownload: (file: BTFileSystem) => Promise<void>;
    onAddClicked?: () => void;
    onViewPermissionsUpdate?: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    onNotify?: (file: BTFileSystem, loginType: BTLoginTypes) => Promise<void>;
    onFileDraw?: (
        docInstanceId: number,
        annotationType: AnnotationType,
        mediaType: MediaType,
    ) => Promise<void>;
    onEditOnline?: (docInstanceId: number, mediaType: MediaType) => Promise<void>;
    onFileDelete?: (file: BTFileSystem) => Promise<void>;
    onShowMore?: (isShowingMore: boolean) => void;
    isReadOnly?: boolean;
    sharingSettings?: SharingSettingsAPI;
    isShowingMore?: boolean;
    hasStickyHeader?: boolean;
    /**
     * Options from the entity attached files used for determining if the show/hide menu items should be displayed.
     */
    fileViewOptions?: FileViewOptions;
    entityId: number;
    entityName: string;
    isOnExternalPage?: boolean;
    onSetMainPhoto?: (file: BTFileSystem) => Promise<void>;
    selectedFileIds?: number[];
    onSelectedFileChange?: (fileId: number, isSelected: boolean) => void;
    /**
     * Array of file IDs that should not be deletable in the context menu.
     * The "Move to trash" menu item will be hidden for these files.
     */
    nonDeletableFileIds?: number[];
    scroll?: Parameters<typeof BTTable>[0]["scroll"];
    /**
     * The id of the file currently being inline-renamed, or null if none.
     * Pair with `onSubmitRename`, `onCancelRename`, and `onClickRename` to enable rename.
     */
    renamingId?: number | null;
    isSavingRename?: boolean;
    onSubmitRename?: (file: BTFileSystem, newTitle: string) => Promise<void>;
    onCancelRename?: () => void;
    onClickRename?: (file: BTFileSystem) => void;
    onSortChange?: (columnField: string | undefined, order: SortOrder | undefined) => void;
    highlightedFileId?: number;
    /**
     * When provided, rows become drag-and-drop sortable.
     * Called with the full reordered file array when the user drops a row in a new position.
     */
    onReorderFiles?: (files: BTFileSystem[]) => void;
}

const MAX_VISIBLE_FILES = 3;

interface ISortableTbodyProps {
    className?: string;
    files: BTFileSystem[];
    onReorderFiles: (files: BTFileSystem[]) => void;
    children: React.ReactNode;
}

/**
 * A <tbody> that initializes SortableJS directly on the DOM element, avoiding ReactSortable's
 * child-to-list index mapping which breaks when rc-table injects synthetic rows (e.g. measure row).
 * All children are rendered as-is; the `.ant-table-measure-row` is excluded from dragging via
 * SortableJS's `filter` option.
 */
const SortableTbody: React.FC<ISortableTbodyProps> = ({
    className,
    files,
    onReorderFiles,
    children,
}) => {
    const tbodyRef = useRef<HTMLTableSectionElement>(null);
    const filesRef = useRef(files);
    useLayoutEffect(() => {
        filesRef.current = files;
    }, [files]);

    useEffect(() => {
        const el = tbodyRef.current;
        if (!el) return;

        const sortable = Sortable.create(el, {
            handle: ".DragAndDropHandle",
            ghostClass: "Placeholder",
            dragClass: "DraggedItem",
            animation: 150,
            filter: ".ant-table-measure-row",
            onEnd: (event) => {
                const { oldIndex, newIndex } = event;
                if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;

                // Read final order from DOM — SortableJS has already moved the rows.
                // Use data-row-key (set by Ant Design) to map rows back to file objects.
                const rows = Array.from(el.querySelectorAll("tr[data-row-key]"));
                const currentFiles = filesRef.current;
                const reordered = rows
                    .map((row) => {
                        const key = Number(row.getAttribute("data-row-key"));
                        return currentFiles.find((f) => f.id === key);
                    })
                    .filter((f): f is BTFileSystem => f != null);

                if (reordered.length === currentFiles.length) {
                    onReorderFiles(reordered);
                }
            },
        });

        return () => sortable.destroy();
    }, [onReorderFiles]);

    return (
        <tbody ref={tbodyRef} className={className}>
            {children}
        </tbody>
    );
};

export const MediaTable = ({
    files,
    onVideoDownload,
    onViewPermissionsUpdate,
    onNotify,
    onFileDraw,
    onEditOnline,
    onFileDelete,
    isReadOnly,
    sharingSettings,
    onOpenLightbox,
    onFileClick,
    onShowMore,
    fileViewOptions,
    isShowingMore,
    entityId,
    entityName,
    hasStickyHeader = true,
    isOnExternalPage = false,
    onSetMainPhoto,
    selectedFileIds,
    onSelectedFileChange,
    nonDeletableFileIds,
    scroll,
    renamingId,
    isSavingRename,
    onSubmitRename,
    onCancelRename,
    onClickRename,
    onSortChange,
    highlightedFileId,
    onReorderFiles,
}: IMediaTableProps) => {
    const userInfo = useContext(UserInfoContext);
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const webview = useWebviewContext();

    const [showAllFiles, setShowAllFiles] = useState(false);
    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const [isSorted, setIsSorted] = useState(false);

    const isControlled = isShowingMore !== undefined && onShowMore !== undefined;
    const [showFiles, setShowFiles] = isControlled
        ? [!!isShowingMore, onShowMore as Dispatch<SetStateAction<boolean>>]
        : [showAllFiles, setShowAllFiles];

    const viewableFiles = showFiles ? files : files.slice(0, MAX_VISIBLE_FILES);
    const showViewAll = viewableFiles.length !== files.length && !showFiles;
    const showViewLess = viewableFiles.length > MAX_VISIBLE_FILES && showFiles;
    const dragAndDropEnabled = !!onReorderFiles && !isSorted && !showViewAll;

    useEffect(() => {
        if (!showFiles && files.length > MAX_VISIBLE_FILES) {
            setShowFiles(false);
        }
        if (showFiles && files.length <= MAX_VISIBLE_FILES) {
            setShowFiles(true);
        }
    }, [files.length, setShowFiles, showFiles]);

    const handleViewAll = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setScrollPosition(window.scrollY);
            setShowFiles(true);
        },
        [setShowFiles],
    );

    const handleViewLess = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setShowFiles(false);
            window.scrollTo(0, scrollPosition);
        },
        [setShowFiles, scrollPosition],
    );

    const handleFileClicked = useAttachedFileClickHandler({
        entityName,
        entityId,
        onOpenLightbox,
        onFileClick,
    });

    const handleClickThumbnail = useCallback(
        (e: React.MouseEvent<HTMLImageElement, MouseEvent>, file: BTFileSystem) => {
            // stop row from being selected
            e.stopPropagation();
            handleFileClicked(file);
        },
        [handleFileClicked],
    );

    const dragHandleColumn: IBTColumnProps<BTFileSystem> = useMemo(
        () => ({
            width: "4%",
            dataIndex: "",
            title: "",
            render: () => <DragAndDropHandle />,
        }),
        [],
    );

    const contextMenuColumn: IBTColumnProps<BTFileSystem> = useMemo(() => {
        return {
            width: "6%",
            dataIndex: "",
            title: "",
            render: (_, file) => {
                const items = getAttachmentOverlayItems(
                    file,
                    !!isReadOnly,
                    userInfo?.loginType,
                    sharingSettings,
                    !onNotify,
                    !onFileDraw || isOnExternalPage,
                    !onEditOnline,
                    fileViewOptions,
                    isOnExternalPage,
                    nonDeletableFileIds,
                    !!webview,
                    !!onClickRename,
                );
                const hasContextItems = areContextItemsPresent(items);
                if (!hasContextItems) {
                    return <HiddenMediaContextMenu />;
                }
                return (
                    <MediaContextMenu
                        renderOverlay={(onMenuClick) => (
                            <AttachmentOverlay
                                items={items}
                                file={file}
                                isReadOnly={isReadOnly}
                                onClickDownloadVideo={onVideoDownload}
                                onFilePermissionsUpdate={onViewPermissionsUpdate}
                                onClickNotify={onNotify}
                                onClickDraw={onFileDraw}
                                onClickEditOnline={onEditOnline}
                                onClickDelete={onFileDelete}
                                onMenuClick={onMenuClick}
                                onClickSetMainPhoto={onSetMainPhoto}
                                onClickRename={onClickRename}
                            />
                        )}
                        key={`mediaTable-ContextMenuColumn-${file.id}`}
                        file={file}
                    />
                );
            },
        };
    }, [
        isReadOnly,
        userInfo?.loginType,
        sharingSettings,
        onNotify,
        onFileDraw,
        onEditOnline,
        onVideoDownload,
        onViewPermissionsUpdate,
        onFileDelete,
        fileViewOptions,
        onSetMainPhoto,
        isOnExternalPage,
        nonDeletableFileIds,
        webview,
        onClickRename,
    ]);

    const fileColumns: IBTColumnProps<BTFileSystem>[] = useMemo(() => {
        const columns: IBTColumnProps<BTFileSystem>[] = [];
        if (dragAndDropEnabled) {
            columns.push(dragHandleColumn);
        }
        columns.push(
            thumbnailColumn({
                onClick: handleClickThumbnail,
                width: "8%",
                canClickFile: (file) => canClickFile(file, entityId),
            }),
            titleColumn({
                onClick: handleFileClicked,
                canClickFile: (file) => canClickFile(file, entityId),
                renamingId,
                isSavingRename,
                onSubmitRename,
                onCancelRename,
            }),
        );
        if (
            !appDefaultInfo?.attachedFilesRemoveViewableByColumn &&
            userInfo?.loginType === BTLoginTypes.BUILDER
        ) {
            const showOptions = getShowOptions(fileViewOptions);
            const subOptions = getSubsOptions(showOptions);
            const clientOptions = getOwnerOptions(showOptions);
            const canShowSubs = subOptions?.canEdit && subOptions?.visible;
            const canShowClients = clientOptions?.canEdit && clientOptions?.visible;
            if (canShowClients || canShowSubs) {
                columns.push(
                    permissionsColumn({
                        width: "12%",
                        fileViewOptions,
                        loginType: userInfo?.loginType,
                    }),
                );
            }
        }
        columns.push(
            addedByColumn({ width: "15%" }),
            lastModifiedColumn({ width: "21%" }),
            contextMenuColumn,
        );
        return columns;
    }, [
        handleClickThumbnail,
        handleFileClicked,
        appDefaultInfo,
        userInfo?.loginType,
        contextMenuColumn,
        fileViewOptions,
        entityId,
        renamingId,
        isSavingRename,
        onSubmitRename,
        onCancelRename,
        dragAndDropEnabled,
        dragHandleColumn,
    ]);

    const getFileRow = useCallback(
        (props) => {
            const fileId = props["data-row-key"];
            const file = files.find((file) => file.id === fileId);
            // If somehow the file is not found, return a default row
            // The file should always be present in the files array though
            if (!file) {
                return <tr {...props} />;
            }

            const items = getAttachmentOverlayItems(
                file,
                !!isReadOnly,
                userInfo?.loginType,
                sharingSettings,
                !onNotify,
                !onFileDraw || isOnExternalPage,
                !onEditOnline,
                fileViewOptions,
                isOnExternalPage,
                nonDeletableFileIds,
                !!webview,
                !!onClickRename,
            );

            return (
                <MediaContextMenu
                    renderOverlay={(onMenuClick) => (
                        <AttachmentOverlay
                            items={items}
                            file={file}
                            isReadOnly={isReadOnly}
                            onClickDownloadVideo={onVideoDownload}
                            onFilePermissionsUpdate={onViewPermissionsUpdate}
                            onClickNotify={onNotify}
                            onClickDraw={onFileDraw}
                            onClickEditOnline={onEditOnline}
                            onClickDelete={onFileDelete}
                            onMenuClick={onMenuClick}
                            onClickSetMainPhoto={onSetMainPhoto}
                            onClickRename={onClickRename}
                        />
                    )}
                    file={file}
                    key={`mediaTable-ContextMenuRow-${file.id}`}
                >
                    <tr {...props} data-testid={`file-row-${fileId}`} />
                </MediaContextMenu>
            );
        },
        [
            files,
            isReadOnly,
            userInfo?.loginType,
            sharingSettings,
            onNotify,
            onFileDraw,
            isOnExternalPage,
            onEditOnline,
            fileViewOptions,
            onVideoDownload,
            onViewPermissionsUpdate,
            onFileDelete,
            onSetMainPhoto,
            nonDeletableFileIds,
            webview,
            onClickRename,
        ],
    );

    const getTableBodyWrapper = useCallback(
        (wrapperProps: React.HTMLAttributes<HTMLElement>) => {
            if (!dragAndDropEnabled || !onReorderFiles) {
                return <tbody className={wrapperProps.className}>{wrapperProps.children}</tbody>;
            }
            return (
                <SortableTbody
                    className={wrapperProps.className}
                    files={viewableFiles}
                    onReorderFiles={onReorderFiles}
                >
                    {wrapperProps.children}
                </SortableTbody>
            );
        },
        [viewableFiles, onReorderFiles, dragAndDropEnabled],
    );

    const handleSortChange = useCallback(
        (
            _pagination: unknown,
            _filters: unknown,
            sorter: SorterResult<BTFileSystem> | SorterResult<BTFileSystem>[],
        ) => {
            if (!onSortChange) return;
            const mainSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const columnField = mainSorter.field != null ? String(mainSorter.field) : undefined;
            const order = mainSorter.order ?? undefined;
            setIsSorted(order != null);
            onSortChange(columnField, order);
        },
        [onSortChange],
    );

    const canSelectFiles = canMultiSelectFiles(files, isOnExternalPage, isReadOnly);
    const allFilesSelected = areAllFilesSelected(files, selectedFileIds);

    const handleSelectAll = useCallback(() => {
        if (onSelectedFileChange) {
            handleSelectAllFiles(files, selectedFileIds, onSelectedFileChange);
        }
    }, [files, selectedFileIds, onSelectedFileChange]);

    const { trackEvent } = useTracking();
    const rowSelection: TableRowSelection<BTFileSystem> | undefined = useMemo(() => {
        if (!onSelectedFileChange || !canSelectFiles) {
            return undefined;
        }
        const filesAreSelected = hasSelectedFiles(selectedFileIds);
        const selection: TableRowSelection<BTFileSystem> = {
            type: "checkbox",
            selectedRowKeys: selectedFileIds,
            onSelect: (record, selected) => {
                trackEvent({
                    event: "ValueChange",
                    uniqueId: `mediaTableSelectFile-${selected}`,
                });
                onSelectedFileChange(record.id, selected);
            },
            getCheckboxProps: (record: BTFileSystem) => ({
                "data-testid": `MediaTableSelectCheckbox-${record.id}`,
                disabled: !record.permissions?.canDelete,
            }),
            columnTitle: (
                <BTCheckbox
                    id="MediaTableSelectAllFilesCheckbox"
                    data-testid="MediaTableSelectAllFilesCheckbox"
                    checked={filesAreSelected}
                    indeterminate={filesAreSelected && !allFilesSelected}
                    onChange={handleSelectAll}
                />
            ),
        };
        return selection;
    }, [
        onSelectedFileChange,
        canSelectFiles,
        selectedFileIds,
        allFilesSelected,
        handleSelectAll,
        trackEvent,
    ]);

    const tableContent = (
        <>
            <BTTable<BTFileSystem>
                data-testid="mediaTable"
                className="MediaTable"
                // Don't change the row key, it is used to show the context menu correctly
                rowKey={(row) => row.id}
                rowClassName={(row) =>
                    cn("FileRow", row.id === highlightedFileId && "FileRow--active")
                }
                dataSource={viewableFiles}
                columns={fileColumns}
                sticky={hasStickyHeader ? { topLevel: 0 } : undefined}
                rowSelection={rowSelection}
                components={{
                    body: {
                        wrapper: getTableBodyWrapper,
                        row: getFileRow,
                    },
                }}
                pagination={false}
                size="small"
                emptyText="No attachments"
                {...(onSortChange ? { onChange: handleSortChange } : {})}
                {...(scroll ? { scroll } : {})}
            />
            {(showViewAll || showViewLess) && (
                <BTRow justify="center" className="padding-top-sm">
                    {showViewAll && (
                        <BTButton
                            data-testid="viewAllFiles"
                            type="tertiary"
                            onClick={handleViewAll}
                        >
                            {`View all (${files.length})`}
                        </BTButton>
                    )}
                    {showViewLess && (
                        <BTButton
                            data-testid="viewLessFiles"
                            type="tertiary"
                            onClick={handleViewLess}
                        >
                            View less
                        </BTButton>
                    )}
                </BTRow>
            )}
        </>
    );

    return tableContent;
};
